export type FormatType = "bankcard" | "money" | "moneyVol2" | "custom";

export type NumberSeparatorConfig = {
  value: string;
  type?: FormatType;
  groupSize?: number;
  separator?: string;
  direction?: "ltr" | "rtl";
  decimals?: number; // Maximum number of decimal places to show (0 means no decimals)
  decimalSeparator?: string; // Character to use as decimal separator
};

type FormatConfig = {
  groupSize: number;
  separator: string;
  direction: "ltr" | "rtl";
  decimals: number;
  decimalSeparator: string;
};

const FORMAT_CONFIGS: Record<
  FormatType,
  Omit<FormatConfig, "decimals" | "decimalSeparator">
> = {
  bankcard: { groupSize: 4, separator: "-", direction: "ltr" },
  money: { groupSize: 3, separator: ",", direction: "rtl" },
  moneyVol2: { groupSize: 3, separator: "،", direction: "rtl" },
  custom: { groupSize: 0, separator: "", direction: "ltr" },
};

const isValidNumber = (value: string): boolean => {
  // Remove all non-digit, non-decimal point, non-minus characters
  const cleaned = value?.replace(/[^\d.-]/g, "");

  // Check if it matches number pattern
  const numberPattern = /^-?\d*\.?\d*$/;

  if (!numberPattern.test(cleaned)) return false;
  if (cleaned === "" || cleaned === "." || cleaned === "-") return false;

  // Check for multiple decimal points
  const decimalCount = (cleaned.match(/\./g) || []).length;
  if (decimalCount > 1) return false;

  // Check for multiple minus signs or minus not at start
  const minusCount = (cleaned.match(/-/g) || []).length;
  if (minusCount > 1 || (minusCount === 1 && cleaned[0] !== "-")) return false;

  return true;
};

export const numberSeparator = ({
  value: valueInput,
  type = "custom",
  groupSize,
  separator,
  direction,
  decimals = 0,
  decimalSeparator = ".",
}: NumberSeparatorConfig): string => {
  const defaultConfig = FORMAT_CONFIGS[type];
  const finalConfig: FormatConfig = {
    groupSize: groupSize ?? defaultConfig.groupSize,
    separator: separator ?? defaultConfig.separator,
    direction: direction ?? defaultConfig.direction,
    decimals,
    decimalSeparator,
  };

  const value = String(valueInput);

  if (!isValidNumber(value)) return value;

  const numValueArray = value.split(".");
  let integerPart = numValueArray[0];
  const decimalPart = numValueArray[1] || "";

  integerPart = integerPart.replace(/\D/g, "");

  let formattedDecimal = "";
  if (finalConfig.decimals > 0 && decimalPart) {
    const trimmedDecimal = decimalPart.replace(/0+$/, "");

    if (trimmedDecimal.length > 0) {
      formattedDecimal = trimmedDecimal.slice(0, finalConfig.decimals);
    }
  }

  const formattedInteger =
    finalConfig.direction === "rtl"
      ? formatRTL(integerPart || "0", finalConfig)
      : formatLTR(integerPart || "0", finalConfig);

  return formattedDecimal
    ? `${formattedInteger}${finalConfig.decimalSeparator}${formattedDecimal}`
    : formattedInteger;
};

const formatRTL = (input: string, config: FormatConfig): string => {
  const { groupSize, separator } = config;
  const chunks: string[] = [];

  for (let i = input.length; i > 0; i -= groupSize) {
    const start = Math.max(i - groupSize, 0);
    chunks.unshift(input.slice(start, i));
  }
  return chunks.join(separator);
};

const formatLTR = (input: string, config: FormatConfig): string => {
  const { groupSize, separator } = config;
  const regex = new RegExp(`.{1,${groupSize}}`, "g");
  return input.match(regex)?.join(separator) ?? input;
};
